import { isBoolean, isNumber } from '../fold-helpers';
import { createWidgetNormalizer } from '../create-widget-normalizer';

/** A spacer is the one widget everyone has a different word for. */
const HEIGHT_SYNONYM_KEYS = ['spacing', 'space', 'gap', 'size'] as const;
const MOBILE_HEIGHT_SYNONYM_KEYS = ['mobileSpacing', 'mobileGap', 'mobileSize'] as const;

/**
 * The normalization shared by `create_indent_widget` and `update_indent_widget`. Two classes of
 * miss:
 *
 * - **flat placement**: the model sends `{ columnId, height: 60 }` instead of nesting height under
 *   `data`. This is a KNOWN, observed failure for this tool specifically — the create operation's
 *   own description carries a prompt-level workaround for it ("The ONLY top-level parameters are
 *   columnId, index and data; there is no top-level height"), which is a hint that prose alone
 *   wasn't winning. A plain `z.object()` strips the stray top-level key silently, so the call
 *   used to succeed as a DEFAULT 40px spacer — right widget, wrong height, reported as success.
 * - **synonyms**: `spacing`/`space`/`gap`/`size` for the height itself.
 *
 * Height is deliberately NOT coerced from a numeric string here: `accepts: isNumber` means a `"60"`
 * synonym never becomes the height by way of this fold. It is still handed to the schema under the
 * key it belongs to, though — dropping it left `optionalData` with nothing to keep and the call
 * answered as a default 40px spacer, reporting nothing — so the type error that follows is real,
 * and `validateForgiving` in front of the routes is what turns the everyday `"60"` into `60`.
 *
 * `optionalData`: a bare spacer is a perfectly meaningful call (40px, synchronized), so a caller
 * that sent no `data` and no flat field to fold into one must not gain an invented `{}`.
 */
const indentNormalizer = createWidgetNormalizer({
    rules: [
        { target: 'height', synonyms: HEIGHT_SYNONYM_KEYS, accepts: isNumber },
        { target: 'mobileHeight', synonyms: MOBILE_HEIGHT_SYNONYM_KEYS, accepts: isNumber },
        { target: 'isSynchronizeStyle', accepts: isBoolean },
    ],
    optionalData: true,
});

export function normalizeIndentWidgetArgs(raw: unknown): unknown {
    return indentNormalizer.normalize(raw);
}
